import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { spotApi } from "@/api";
import { ApiError } from "@/api/client";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import type { AdminClaim } from "@/types";
import i18n from "@/i18n";

export default function AdminClaims() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const [claims, setClaims] = useState<AdminClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    // No `setLoading(true)` here. This has no dependencies and is called only
    // by the mount effect below, so it ran exactly once — at a point where
    // `loading` was already `true` from its initialiser. The write did nothing
    // except make an effect set state synchronously. A refetch path that needs
    // the spinner back should set it at that call site, where it is true.
    try {
      setClaims(await spotApi.admin.pendingClaims());
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setForbidden(true);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, approve: boolean) {
    setBusyId(id);
    try {
      await (approve ? spotApi.admin.approve(id) : spotApi.admin.reject(id));
      setClaims((prev) => prev.filter((claim) => claim.id !== id));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable onPress={() => router.replace("/business")} hitSlop={10}>
          <Ionicons name="chevron-back" size={26} color={c.text} />
        </Pressable>
        <ThemedText variant="bodyStrong">
          {i18n.t("business.admin.title")}
        </ThemedText>
        <View style={{ width: 26 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        {loading ? (
          <ActivityIndicator color={c.tint} style={styles.loader} />
        ) : forbidden ? (
          <Card c={c}>
            <ThemedText variant="body" tone="secondary" center>
              {i18n.t("business.admin.accessRequired")}
            </ThemedText>
          </Card>
        ) : claims.length === 0 ? (
          <Card c={c}>
            <ThemedText variant="body" tone="secondary" center>
              {i18n.t("business.admin.empty")}
            </ThemedText>
          </Card>
        ) : (
          claims.map((claim) => (
            <View
              key={claim.id}
              style={[
                styles.claim,
                { backgroundColor: c.surface, borderColor: c.border },
              ]}
            >
              <ThemedText variant="bodyStrong">{claim.venueName}</ThemedText>
              {claim.venueAddress ? (
                <ThemedText variant="caption" tone="secondary">
                  {claim.venueAddress}
                </ThemedText>
              ) : null}
              <ThemedText variant="caption" tone="muted">
                {claim.ownerEmail ?? i18n.t("business.admin.unknownOwner")}
              </ThemedText>

              <View style={styles.actions}>
                <Pressable
                  onPress={() => decide(claim.id, false)}
                  disabled={busyId === claim.id}
                  style={[styles.rejectBtn, { borderColor: c.border }]}
                >
                  <ThemedText variant="caption" tone="danger">
                    {i18n.t("business.admin.reject")}
                  </ThemedText>
                </Pressable>
                <Pressable
                  onPress={() => decide(claim.id, true)}
                  disabled={busyId === claim.id}
                  style={[styles.approveBtn, { backgroundColor: c.tint }]}
                >
                  {busyId === claim.id ? (
                    <ActivityIndicator size="small" color={c.onTint} />
                  ) : (
                    <ThemedText variant="caption" tone="onTint">
                      {i18n.t("business.admin.approve")}
                    </ThemedText>
                  )}
                </Pressable>
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function Card({
  c,
  children,
}: {
  c: ReturnType<typeof useTheme>;
  children: React.ReactNode;
}) {
  return (
    <View
      style={[
        styles.card,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  content: {
    width: "100%",
    maxWidth: 640,
    alignSelf: "center",
    padding: Spacing.xl,
    gap: Spacing.md,
  },
  loader: { marginVertical: Spacing.xl },
  card: {
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  claim: {
    gap: 3,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  actions: {
    flexDirection: "row",
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  rejectBtn: {
    minHeight: 40,
    paddingHorizontal: Spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  approveBtn: {
    minWidth: 96,
    minHeight: 40,
    paddingHorizontal: Spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.md,
  },
});
